import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
function adminHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('hrv_admin_token')}` };
}

const EMPTY_FORM = {
  condition: 'high', severity: 'moderate',
  interpretation: '', dietary_advice: '', lifestyle_advice: '',
  followup_tests: '', see_doctor_if: '',
};

function QuickAddModal({ testName, onSave, onClose }) {
  const [form, setForm]     = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}/admin/unmatched-tests/${encodeURIComponent(testName)}/create-interpretation`, {
        method: 'POST', headers: adminHeaders(), body: JSON.stringify(form),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      onSave(); onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: '16px' }}>
      <div style={{ background: 'white', borderRadius: '14px', width: '100%', maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '22px 28px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700' }}>Create Interpretation</h3>
            <div style={{ color: '#6366f1', fontWeight: '600', marginTop: '2px', fontSize: '0.9rem' }}>{testName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' }}>×</button>
        </div>
        <div style={{ padding: '24px 28px' }}>
          {error && <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', color: '#c53030', padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.875rem' }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            {[
              ['Condition *', 'condition', ['high', 'low', 'positive', 'abnormal', 'normal']],
              ['Severity', 'severity', ['', 'mild', 'moderate', 'severe', 'critical']],
            ].map(([label, name, opts]) => (
              <div key={name}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.78rem', color: '#4a5568', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
                <select value={form[name]} onChange={e => set(name, e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', background: 'white' }}>
                  {opts.map(o => <option key={o} value={o}>{o || '— select —'}</option>)}
                </select>
              </div>
            ))}
          </div>

          {[
            ['Interpretation', 'interpretation', 3],
            ['Dietary Advice', 'dietary_advice', 2],
            ['Lifestyle Advice', 'lifestyle_advice', 2],
            ['Follow-up Tests', 'followup_tests', 2],
            ['See Doctor If', 'see_doctor_if', 2],
          ].map(([label, name, rows]) => (
            <div key={name} style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.78rem', color: '#4a5568', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
              <textarea value={form[name]} onChange={e => set(name, e.target.value)} rows={rows}
                style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
          ))}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button onClick={onClose} style={{ padding: '10px 20px', background: '#edf2f7', color: '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
              {saving ? 'Saving…' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminUnmatched() {
  const [tests, setTests]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);

  const fetchTests = () => {
    setLoading(true);
    fetch(`${API_URL}/admin/unmatched-tests`, { headers: adminHeaders() })
      .then(r => r.json())
      .then(d => { setTests(d.unmatched_tests || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchTests(); }, []);

  return (
    <AdminLayout>
      {modal && <QuickAddModal testName={modal} onSave={fetchTests} onClose={() => setModal(null)} />}

      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: '700', color: '#1a202c', margin: 0 }}>Unmatched Test Searches</h1>
        <p style={{ color: '#718096', margin: '6px 0 0' }}>
          Tests users searched for that weren't in our interpretation database — your content roadmap
        </p>
      </div>

      {tests.length === 0 && !loading ? (
        <div style={{ background: 'white', borderRadius: '12px', padding: '60px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✅</div>
          <h3 style={{ color: '#2d3748', margin: '0 0 8px' }}>No unmatched tests</h3>
          <p style={{ color: '#718096', margin: 0 }}>All test searches are covered by your interpretation database.</p>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['Test Name', 'Searches', 'First Seen', 'Last Seen', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.78rem', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>Loading…</td></tr>
              ) : tests.map((t, i) => (
                <tr key={t.test_name} style={{ borderBottom: '1px solid #f0f4f8', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={{ padding: '12px 16px', fontWeight: '600', color: '#2d3748', fontSize: '0.9rem' }}>{t.test_name}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: '32px', height: '24px', padding: '0 8px',
                      borderRadius: '12px', fontSize: '0.8rem', fontWeight: '700',
                      background: t.frequency >= 5 ? '#fff5f5' : t.frequency >= 2 ? '#fffbeb' : '#f7fafc',
                      color: t.frequency >= 5 ? '#c53030' : t.frequency >= 2 ? '#744210' : '#4a5568',
                    }}>
                      {t.frequency}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#718096', fontSize: '0.8rem' }}>
                    {t.first_seen ? new Date(t.first_seen).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#718096', fontSize: '0.8rem' }}>
                    {t.last_seen ? new Date(t.last_seen).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button onClick={() => setModal(t.test_name)} style={{
                      padding: '6px 14px', background: '#6366f1', color: 'white',
                      border: 'none', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: '600',
                    }}>
                      + Create Interpretation
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
